import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AxiosResultDefaultError } from '../../../../api/request';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { TranslationsKeys } from '../../../../locales/translations';
import { getAuthorizationFormData } from '../api';
import { HomologationFormDataResponse } from '../types';

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  HomologationFormDataResponse,
  AxiosResultDefaultError
>();

export function useAuthorizationFilterLoader() {
  return useFetch();
}

export default function AuthorizationFilterLoader({
  children,
}: {
  children: ReactElement | ReactElement[];
}) {
  const { t } = useTranslation(TranslationsKeys.HOMOLOGATION_AUTHORIZATION);

  const request = useCallback(async () => {
    return getAuthorizationFormData();
  }, []);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) {
            return <Loading insideCard />;
          } else if (error) {
            const isForbidden = 'status' in error && error.status === 403;
            return (
              <DisplayError
                insideCard
                title={
                  isForbidden ? t('error.unauthorizedTitle') : t('error.title')
                }
                textBody={
                  isForbidden ? t('error.unauthorizedBody') : t('error.body')
                }
                retryAction={isForbidden ? undefined : refresh}
              />
            );
          } else if (data) {
            return <>{children}</>;
          }
          return null;
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
